/**
 * Quotation revisions — `quoflow.quoflow.quoflow.api.quotation.{get_revisions,
 * log_revision_request,apply_revisions}` (Task 11). Live shapes verified 2026-08-04:
 *
 *   GET  quotation.get_revisions?quotation=   → {revision, requests: [{revision_no, type,
 *                                                 item_code, qty, detail, status, raised_on}]}
 *   POST quotation.log_revision_request         {quotation, type, item_code, qty, detail,
 *                                                 communication}
 *   POST quotation.apply_revisions               {quotation, changes: [{op, item_code, qty,
 *                                                 uom?}]} → {revision, status}
 *
 * ---- no request id anywhere on this wire ----
 *
 * See `RevisionRequest`'s own header comment in `../types`: `get_revisions` gives no stable per-
 * request id, and `apply_revisions` has no `request_id` field to close one with either — it
 * resolves a change purely by `item_code`. This file therefore does NOT attempt to close a
 * specific request as part of applying a change (there is nothing to send that would do it); a
 * request's `status` flipping to `done` is entirely the SERVER's business now, presumably by
 * matching an applied change back to an open request with the same `item_code`/`type` — an
 * inference, not a verified behaviour, flagged in task-11-report.md.
 *
 * ---- "reprice" no longer applies through this file ----
 *
 * `apply_revisions`'s `changes[]` carries no price field at all (`RevisionChange`'s own comment).
 * A "reprice" customer request is resolved by `useUpdateQuotationLine`
 * (`useQuotation.ts`) instead — `RevisionsPage` calls that hook directly for a `reprice` row, not
 * `useApplyRevisions`. One consequence worth stating plainly: because that PATCH has no way to
 * name which revision request it satisfies either, a `reprice` row applied this way has no
 * server-side path to `done` at all right now — it stays `open` on this screen until the backend
 * grows one.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { callGet, callPost } from './bosun'
import type { BosunApiError } from './errors'
import { num, str } from './wire'
import { qk } from './keys'
import { REVISION_TYPES } from '../types'
import { toQuotationStatus } from './useQuotation'
import type { ApplyRevisionsResponse, RevisionChange, RevisionRequest, RevisionType, RevisionsResponse } from '../types'

/* ============================================================================================
 * quotation.get_revisions → useRevisions
 * ============================================================================================ */

export interface WireRevisionRequest {
  revision_no: number
  type: string
  item_code: string | null
  qty: number | null
  detail: string
  status: string
  raised_on: string
}

export interface WireRevisionsResponse {
  revision: number
  requests: WireRevisionRequest[]
}

/** An unrecognised `type` degrades to `'set_qty'` rather than crashing the row — same defensive
 * read the old dead `/api/v1` route validated against (`mock-server/routes/revisions.ts`). */
function toRevisionType(value: string): RevisionType {
  return (REVISION_TYPES as readonly string[]).includes(value) ? (value as RevisionType) : 'set_qty'
}

export function toRevisionRequest(w: WireRevisionRequest, index: number): RevisionRequest {
  return {
    // No stable id on this wire — see this file's own header and `RevisionRequest`'s comment in
    // ../types. Synthesized from position + round + item so it is stable for a given response;
    // never sent back to the server.
    id: `${num(w.revision_no)}-${index}-${str(w.item_code) || 'x'}`,
    revision_no: num(w.revision_no),
    type: toRevisionType(str(w.type)),
    item_code: w.item_code ?? null,
    qty: w.qty === null || w.qty === undefined ? null : num(w.qty),
    detail: str(w.detail),
    status: str(w.status) === 'done' ? 'done' : 'open',
    raised_on: str(w.raised_on),
  }
}

export function toRevisionsResponse(w: WireRevisionsResponse): RevisionsResponse {
  return {
    revision: num(w.revision),
    requests: (w.requests ?? []).map(toRevisionRequest),
  }
}

export function useRevisions(quotationId: string | null | undefined) {
  return useQuery({
    queryKey: qk.revisions(quotationId ?? ''),
    queryFn: async () =>
      toRevisionsResponse(await callGet<WireRevisionsResponse>('quotation', 'get_revisions', { quotation: quotationId })),
    enabled: !!quotationId,
  })
}

/* ============================================================================================
 * quotation.log_revision_request → useLogRevisionRequest
 * ============================================================================================ */

export interface LogRevisionRequestInput {
  type: RevisionType
  item_code?: string | null
  qty?: number | null
  detail: string
  /** The inbound mail this request was raised from, when there is one (contract §9's audit
   * trail — same idea `enquiry_sources` gives an enquiry's own extraction). */
  communication?: string
}

async function logRevisionRequest(quotationId: string, input: LogRevisionRequestInput): Promise<void> {
  await callPost('quotation', 'log_revision_request', {
    quotation: quotationId,
    type: input.type,
    item_code: input.item_code ?? null,
    qty: input.qty ?? null,
    detail: input.detail,
    communication: input.communication,
  })
}

/**
 * NOTHING IN THE APP CALLS THIS as of 2026-08-09, deliberately.
 *
 * A "log the customer's request, then apply it" step was built on the Revisions screen and
 * removed the same day: with the quotation directly editable on that screen, logging a request
 * first is a form standing between the purchaser and the edit they were going to make anyway.
 * Changing a quantity, repricing a line or adding an item is now done on the quotation itself.
 *
 * Kept because the method is real and the flow it belongs to is the BACKEND's: a customer's
 * revision email is what should populate `revision_log` (hence `log_revision_request`'s
 * `communication` field), and `get_revisions` still reads it. If that lands, this is the writer
 * to reuse rather than rewrite. Until then `get_revisions` returns `[]` and the requests table
 * says so honestly.
 */
export function useLogRevisionRequest(
  quotationId: string | undefined,
  options?: { onSuccess?: () => void; onError?: (error: BosunApiError) => void },
) {
  const queryClient = useQueryClient()
  return useMutation<void, BosunApiError, LogRevisionRequestInput>({
    mutationFn: (input) => logRevisionRequest(quotationId as string, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.revisions(quotationId ?? '') })
      options?.onSuccess?.()
    },
    onError: (error) => options?.onError?.(error),
  })
}

/* ============================================================================================
 * quotation.apply_revisions → useApplyRevisions
 * ============================================================================================ */

interface WireApplyRevisionsResult {
  revision: number
  status: string
}

function toWireChange(c: RevisionChange): { op: string; item_code: string; qty: number; uom?: string } {
  return c.uom ? { op: c.op, item_code: c.item_code, qty: c.qty, uom: c.uom } : { op: c.op, item_code: c.item_code, qty: c.qty }
}

async function applyRevisions(quotationId: string, changes: RevisionChange[]): Promise<ApplyRevisionsResponse> {
  const w = await callPost<WireApplyRevisionsResult>('quotation', 'apply_revisions', {
    quotation: quotationId,
    changes: changes.map(toWireChange),
  })
  return { revision: num(w.revision), status: toQuotationStatus(w.status) }
}

/**
 * Applying a change is backend work: it edits the quotation's lines, bumps the revision and
 * returns the quotation to `draft`. So a successful apply invalidates the quotation too — the
 * numbers on the Customer Quotation screen have just moved.
 */
export function useApplyRevisions(
  enquiryId: string,
  quotationId: string | undefined,
  options?: { onSuccess?: (data: ApplyRevisionsResponse) => void; onError?: (error: BosunApiError) => void },
) {
  const queryClient = useQueryClient()
  return useMutation<ApplyRevisionsResponse, BosunApiError, RevisionChange[]>({
    mutationFn: (changes) => applyRevisions(quotationId as string, changes),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: qk.revisions(quotationId ?? '') })
      // The quotation's lines and totals have changed, and it is back to draft. BOTH cache keys —
      // `/revisions` reads through `useExistingQuotation` (`qk.existingQuotationForEnquiry`), a
      // DELIBERATELY separate entry from `qk.quotationForEnquiry` (`useQuotation.ts`'s own
      // header). Missing this one left `/revisions` showing the pre-apply lines — including ones
      // just removed — for up to `POLL_MS` after a successful apply (fix round, final review:
      // I2); `useUpdateQuotationLine`/`useUpdateQuotationTerms` already invalidate both for
      // exactly this reason.
      void queryClient.invalidateQueries({ queryKey: qk.quotationForEnquiry(enquiryId) })
      void queryClient.invalidateQueries({ queryKey: qk.existingQuotationForEnquiry(enquiryId) })
      // Applying a revision is the biggest change this app makes to a quotation; the history
      // panel sits on the same screen and would otherwise not show it until a remount.
      if (quotationId) void queryClient.invalidateQueries({ queryKey: qk.quotationHistory(quotationId) })
      void queryClient.invalidateQueries({ queryKey: qk.enquiry(enquiryId) })
      options?.onSuccess?.(data)
    },
    onError: (error) => options?.onError?.(error),
  })
}

/* ---------- test-only export ---------- */

export const __test__ = { logRevisionRequest, applyRevisions }
